import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { meApi, type MeResponse } from "@/api/me";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { SettingsLayout } from "@/components/settings/SettingsLayout";
import { SettingsCard } from "@/components/settings/SettingsCard";
import { SavingButton } from "@/components/settings/SavingButton";

export function ProfilePage() {
  return (
    <SettingsLayout title="Profile" description="Manage your personal information.">
      <PersonalInformation />
      <AccountInformation />
    </SettingsLayout>
  );
}

function PersonalInformation() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: meApi.get });
  const personalOrg = me?.organizations.find((o) => o.is_personal);
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (personalOrg?.name) setDisplayName(personalOrg.name);
  }, [personalOrg?.name]);

  const mutation = useMutation({
    mutationFn: (name: string) => meApi.patch({ name }),
    onSuccess: (next: MeResponse) => {
      qc.setQueryData(["me"], next);
      toast.success("Profile updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <SettingsCard
      title="Personal information"
      description="Update your display name."
      footer={
        <SavingButton
          saving={mutation.isPending}
          onClick={() => mutation.mutate(displayName)}
          disabled={!displayName || displayName === personalOrg?.name}
        >
          Save
        </SavingButton>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="displayName">Display name</Label>
        <Input
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={80}
        />
      </div>
    </SettingsCard>
  );
}

function AccountInformation() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: meApi.get });
  const memberSince = me?.created_at
    ? new Date(me.created_at).toLocaleDateString(undefined, {
        timeZone: "UTC",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  return (
    <SettingsCard title="Account information" description="Read-only details.">
      <Row label="Email" value={me?.email ?? "—"} />
      <Row label="Member since" value={memberSince} />
    </SettingsCard>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
